import { RefreshIcon } from '@/assets/icons/info-icons/refresh';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { RefreshButton, Results, TopWrapper } from '@/components/table/filterPage/desktop-filter';
import { ISort, SORT_ORDER } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import axios from 'axios';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { LIST_WET_SIGNATURE_UPLOAD_HISTORY_QUERY } from '../queries';
import { WetSignatureUploadHistoryResponseDto } from '../types';
import Table from './table/table';

interface SignaturePacketTabProps {
  sort: ISort;
  onChangeSort: (value: ISort) => void;
}

export const SignaturePacketTab = ({ sort, onChangeSort }: SignaturePacketTabProps) => {
  const params = useParams();
  const { isMobile, isTablet } = useResponsive();

  const [signaturePacketTableData, setSignaturePacketTableData] = useState<WetSignatureUploadHistoryResponseDto[]>([]);
  const [showModalLoader, setShowModalLoader] = useState(false);

  const { loading: listWetSignatureUploadHistoryLoading, refetch: listWetSignatureUploadHistoryRefetch } = useQuery<{
    listWetSignatureUploadHistory: WetSignatureUploadHistoryResponseDto[];
  }>(LIST_WET_SIGNATURE_UPLOAD_HISTORY_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        allocationId: params?.allocationId,
        sortBy: sort.key || 'versionDate',
        orderBy: sort.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC
      }
    },
    onCompleted: (data) => {
      setSignaturePacketTableData(data?.listWetSignatureUploadHistory ?? []);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const viewSignaturePacket = async (fileName: string) => {
    if (!fileName) return;
    try {
      setShowModalLoader(true);
      const { data, headers } = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/allocation/${params?.allocationId}/signed-document/${fileName}`,

        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([data], { type: headers['content-type'] }));
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
    } finally {
      setShowModalLoader(false);
    }
  };

  return (
    <>
      {showModalLoader && (
        <ModalWrappedContent
          title="View Signature Packet"
          isOpen={showModalLoader}
          onClose={() => {}}
          showCloseIcon={false}
          showCancelButton={false}
          isUseOutsideClick={false}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <CustomRefetch isMobile={isMobile} isTablet={isTablet}>
        <Results>{signaturePacketTableData?.length} Results</Results>
        <RefreshButton onClick={() => listWetSignatureUploadHistoryRefetch()}>
          <RefreshIcon fill={themeVar('action.primary')} />
        </RefreshButton>
      </CustomRefetch>
      <Table
        signaturePacketTableData={signaturePacketTableData}
        loading={listWetSignatureUploadHistoryLoading}
        sort={sort}
        setSort={onChangeSort}
        refetch={() => {}}
        viewSignaturePacket={viewSignaturePacket}
      />
    </>
  );
};

const CustomRefetch = styled(TopWrapper)<{ isMobile: boolean; isTablet: boolean }>`
  position: sticky;
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : '0')};
  padding: 50px 0 10px 0;
  background-color: ${themeVar('layer.base')};
  z-index: 10;
`;
